import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import 'htmx.org';

Alpine.plugin(collapse);
window.Alpine = Alpine;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (window.htmx) {
    htmx.config.defaultSwapStyle = 'innerHTML';
    htmx.config.defaultSwapDelay = 0;
    htmx.config.defaultSettleDelay = 200;
    document.body.addEventListener('htmx:configRequest', (event) => {
        event.detail.headers['X-Requested-With'] = 'htmx';
    });
}

function splitHeroTitle() {
    document.querySelectorAll('[data-hero-title]').forEach((el) => {
        const text = el.textContent.trim().replace(/\s+/g, ' ');
        el.setAttribute('aria-label', text);
        el.textContent = '';
        text.split(' ').forEach((word, i) => {
            const span = document.createElement('span');
            span.className = 'hero-word';
            span.style.animationDelay = `${i * 60}ms`;
            span.setAttribute('aria-hidden', 'true');
            span.textContent = word;
            el.appendChild(span);
            el.appendChild(document.createTextNode(' '));
        });
    });
}

function initScrollReveal() {
    // Modern browsers drive .reveal from scroll position in CSS; touching the
    // class here would only fight the animation.
    if (window.CSS && CSS.supports('animation-timeline', 'view()')) {
        return;
    }
    const items = document.querySelectorAll('.reveal');
    if (reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach((el) => el.classList.add('is-visible'));
        return;
    }
    /* Start the fade before the element reaches the screen, not after. Waiting
       for 12% of it to be visible and then running a 700ms transition with an
       unbounded stagger meant the content trailed the scroll: on a short window
       whole sections read as empty blocks. The bottom margin pre-triggers a
       third of a viewport early, and the stagger is capped so a container with
       many children cannot delay the last one by half a second. */
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            const siblings = el.parentElement
                ? Array.from(el.parentElement.querySelectorAll(':scope > .reveal'))
                : [el];
            const index = Math.max(siblings.indexOf(el), 0);
            el.style.transitionDelay = `${Math.min(index, 3) * 60}ms`;
            el.classList.add('is-visible');
            observer.unobserve(el);
        });
    }, { threshold: 0, rootMargin: '0px 0px 33% 0px' });
    items.forEach((el) => observer.observe(el));
}

function initHeroVideo() {
    const video = document.querySelector('video[data-lazy-video]');
    /* The loop is 540 KB of pure decoration on top of the hero still, so it is
       skipped for reduced motion, Save-Data, and metered or slow connections. */
    const link = navigator.connection;
    const expensive = link && (link.saveData || /^([23]g|slow-2g)$/.test(link.effectiveType || ''));
    if (!video || reducedMotion || expensive) {
        if (video) video.remove();
        return;
    }
    const load = () => {
        video.querySelectorAll('source[data-src]').forEach((source) => {
            source.src = source.dataset.src;
        });
        video.load();
        const playAttempt = video.play();
        if (playAttempt) playAttempt.catch(() => video.remove());
    };
    if ('requestIdleCallback' in window) {
        requestIdleCallback(load, { timeout: 3000 });
    } else {
        setTimeout(load, 1500);
    }
}

function initLightbox() {
    const triggers = Array.from(document.querySelectorAll('[data-lightbox]'));
    const overlay = document.querySelector('[data-lightbox-overlay]');
    if (!triggers.length || !overlay) return;

    const groups = new Map();
    triggers.forEach((el) => {
        const name = el.getAttribute('data-lightbox') || 'default';
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(el);
    });

    const img = overlay.querySelector('[data-lb-img]');
    const caption = overlay.querySelector('[data-lb-caption]');
    const counter = overlay.querySelector('[data-lb-counter]');
    const prevBtn = overlay.querySelector('[data-lb-prev]');
    const nextBtn = overlay.querySelector('[data-lb-next]');
    const closeBtn = overlay.querySelector('[data-lb-close]');

    let currentGroup = [];
    let currentIndex = 0;
    let lastFocus = null;

    /* Thumbnails are served responsively, so currentSrc can be a 400w derivative
       that would be stretched across the viewer. Take the widest candidate the
       <img> lists instead - that srcset is the JPEG fallback, so it is safe in
       browsers without AVIF or WebP. */
    function widestSource(inner) {
        if (!inner) return '';
        const candidates = (inner.getAttribute('srcset') || '')
            .split(',')
            .map((part) => part.trim().split(/\s+/))
            .filter((pair) => pair[0])
            .map((pair) => ({ url: pair[0], width: parseInt(pair[1], 10) || 0 }));
        if (!candidates.length) return inner.currentSrc || inner.src || '';
        return candidates.reduce((a, b) => (b.width > a.width ? b : a)).url;
    }

    function itemData(el) {
        const inner = el.tagName === 'IMG' ? el : el.querySelector('img');
        return {
            src: el.getAttribute('data-full') || widestSource(inner),
            caption: el.getAttribute('data-caption') || (inner ? inner.alt : '')
        };
    }

    function show(index) {
        currentIndex = (index + currentGroup.length) % currentGroup.length;
        const data = itemData(currentGroup[currentIndex]);
        img.style.opacity = '0';
        const preload = new Image();
        preload.onload = () => {
            img.src = data.src;
            img.alt = data.caption;
            img.style.opacity = '1';
        };
        preload.onerror = () => {
            img.src = data.src;
            img.alt = data.caption;
            img.style.opacity = '1';
        };
        preload.src = data.src;
        caption.textContent = data.caption;
        counter.textContent = (currentIndex + 1) + ' / ' + currentGroup.length;
    }

    function open(trigger) {
        const name = trigger.getAttribute('data-lightbox') || 'default';
        currentGroup = groups.get(name);
        lastFocus = document.activeElement;
        const multi = currentGroup.length > 1;
        prevBtn.classList.toggle('hidden', !multi);
        nextBtn.classList.toggle('hidden', !multi);
        counter.classList.toggle('hidden', !multi);
        show(currentGroup.indexOf(trigger));
        overlay.classList.remove('hidden');
        overlay.classList.add('flex');
        requestAnimationFrame(() => overlay.classList.remove('opacity-0'));
        document.body.style.overflow = 'hidden';
        closeBtn.focus();
    }

    function close() {
        overlay.classList.add('opacity-0');
        document.body.style.overflow = '';
        setTimeout(() => {
            overlay.classList.add('hidden');
            overlay.classList.remove('flex');
            img.src = '';
        }, 200);
        if (lastFocus) lastFocus.focus();
    }

    let touchX = null;
    overlay.addEventListener('touchstart', (event) => {
        touchX = event.changedTouches[0].clientX;
    }, { passive: true });
    overlay.addEventListener('touchend', (event) => {
        if (touchX === null || currentGroup.length < 2) return;
        const delta = event.changedTouches[0].clientX - touchX;
        if (Math.abs(delta) > 48) show(currentIndex + (delta < 0 ? 1 : -1));
        touchX = null;
    }, { passive: true });

    triggers.forEach((el) => {
        el.addEventListener('click', () => open(el));
        if (el.tagName === 'IMG') {
            el.setAttribute('tabindex', '0');
            el.setAttribute('role', 'button');
            el.classList.add('cursor-zoom-in');
            el.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open(el);
                }
            });
        }
    });
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', () => show(currentIndex - 1));
    nextBtn.addEventListener('click', () => show(currentIndex + 1));
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) close();
    });
    document.addEventListener('keydown', (event) => {
        if (overlay.classList.contains('hidden')) return;
        if (event.key === 'Escape') close();
        if (event.key === 'ArrowLeft') show(currentIndex - 1);
        if (event.key === 'ArrowRight') show(currentIndex + 1);
    });
}

function initAddonModal() {
    const modal = document.querySelector('[data-addon-modal]');
    const triggers = Array.from(document.querySelectorAll('[data-addon]'));
    if (!modal || !triggers.length) return;

    const img = modal.querySelector('[data-addon-img]');
    const name = modal.querySelector('[data-addon-name]');
    const badge = modal.querySelector('[data-addon-badge]');
    const price = modal.querySelector('[data-addon-price]');
    const description = modal.querySelector('[data-addon-description]');
    const details = modal.querySelector('[data-addon-details]');
    const closeBtn = modal.querySelector('[data-addon-close]');
    const decBtn = modal.querySelector('[data-addon-dec]');
    const incBtn = modal.querySelector('[data-addon-inc]');
    const qtyEl = modal.querySelector('[data-addon-qty]');
    const lineEl = modal.querySelector('[data-addon-line]');
    const hintEl = modal.querySelector('[data-addon-hint]');

    let lastFocus = null;
    let currentId = null;
    let unitCents = 0;
    let pricingType = 'per_booking';
    let qty = 0;

    function dollars(cents) {
        const value = cents / 100;
        return '$' + value.toLocaleString('en-US', {
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2
        });
    }

    function bookingContext() {
        const guestsEl = document.getElementById('guests') || document.getElementById('f-guests');
        const fromEl = document.getElementById('sel-from') || document.getElementById('f-from');
        const toEl = document.getElementById('sel-to') || document.getElementById('f-to');
        const guests = guestsEl ? parseInt(guestsEl.value, 10) || 0 : 0;
        let days = 0;
        if (fromEl && toEl && fromEl.value && toEl.value) {
            const from = new Date(fromEl.value + 'T00:00:00');
            const to = new Date(toEl.value + 'T00:00:00');
            const diff = Math.round((to - from) / 86400000) + 1;
            if (Number.isFinite(diff) && diff > 0) days = diff;
        }
        return { guests, days };
    }

    function requestQty(id) {
        let value = 0;
        const handler = (event) => {
            if (event.detail.id === id) value = event.detail.qty;
        };
        window.addEventListener('addon-qty-value', handler);
        window.dispatchEvent(new CustomEvent('addon-qty-request', { detail: { id } }));
        window.removeEventListener('addon-qty-value', handler);
        return value;
    }

    function renderQty() {
        qtyEl.textContent = qty;
        decBtn.disabled = qty <= 0;
        incBtn.disabled = qty >= 9;
        if (qty === 0) {
            lineEl.textContent = '';
            hintEl.textContent = '';
            return;
        }
        const ctx = bookingContext();
        let multiplier = 1;
        let hint = '';
        if (pricingType === 'per_day') {
            multiplier = ctx.days || 1;
            hint = ctx.days ? 'Estimated: × ' + ctx.days + (ctx.days === 1 ? ' day' : ' days') : 'Charged per day';
        } else if (pricingType === 'per_person') {
            multiplier = ctx.guests || 1;
            hint = ctx.guests ? 'Estimated: × ' + ctx.guests + (ctx.guests === 1 ? ' guest' : ' guests') : 'Charged per guest';
        }
        const total = unitCents * qty * multiplier;
        lineEl.textContent = qty + ' × ' + dollars(unitCents) + (multiplier > 1 ? ' × ' + multiplier : '') + ' = ' + dollars(total);
        hintEl.textContent = hint;
    }

    function setQty(next) {
        qty = Math.min(9, Math.max(0, next));
        window.dispatchEvent(new CustomEvent('addon-set-qty', { detail: { id: currentId, qty } }));
        renderQty();
    }

    function open(trigger) {
        lastFocus = document.activeElement;
        currentId = trigger.getAttribute('data-id');
        unitCents = parseInt(trigger.getAttribute('data-price-cents'), 10) || 0;
        pricingType = trigger.getAttribute('data-pricing') || 'per_booking';
        qty = requestQty(currentId);
        img.src = trigger.getAttribute('data-img') || '';
        img.alt = trigger.getAttribute('data-name') || '';
        name.textContent = trigger.getAttribute('data-name') || '';
        price.textContent = trigger.getAttribute('data-price') || '';
        description.textContent = trigger.getAttribute('data-description') || '';
        const badgeText = trigger.getAttribute('data-badge') || '';
        badge.textContent = badgeText;
        badge.classList.toggle('hidden', !badgeText);
        const detailsText = trigger.getAttribute('data-details') || '';
        details.textContent = detailsText;
        details.classList.toggle('hidden', !detailsText);
        renderQty();
        modal.classList.remove('hidden');
        modal.classList.add('flex');
        requestAnimationFrame(() => modal.classList.remove('opacity-0'));
        document.body.style.overflow = 'hidden';
        closeBtn.focus();
    }

    function close() {
        modal.classList.add('opacity-0');
        document.body.style.overflow = '';
        setTimeout(() => {
            modal.classList.add('hidden');
            modal.classList.remove('flex');
            img.src = '';
        }, 200);
        if (lastFocus) lastFocus.focus();
    }

    triggers.forEach((el) => el.addEventListener('click', () => open(el)));
    decBtn.addEventListener('click', () => setQty(qty - 1));
    incBtn.addEventListener('click', () => setQty(qty + 1));
    closeBtn.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
        if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !modal.classList.contains('hidden')) close();
    });
}

function init() {
    if (!reducedMotion) splitHeroTitle();
    initScrollReveal();
    initHeroVideo();
    initLightbox();
    initAddonModal();
    Alpine.start();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
